import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useStore } from "../../context/StoreContext";
import { api } from "services/api";
import { SUPPORTED_PLATFORM_LANGUAGES } from "../../utils/languageConfig";

const AVAILABLE_LANGUAGES = SUPPORTED_PLATFORM_LANGUAGES;

export const LanguageSettings: React.FC = () => {
 const { t } = useTranslation();
 const { store, refreshStore, loading: storeLoading } = useStore();
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [success, setSuccess] = useState<string | null>(null);

 const [selectedLanguages, setSelectedLanguages] = useState<string[]>(["en"]);

 const loading = storeLoading;

 // Initialize form fields when store data is loaded
 useEffect(() => {
  if (store && !storeLoading) {
   setSelectedLanguages((store.supportedLanguages as string[]) || ["en"]);
  } else if (!store && !storeLoading) {
   setSelectedLanguages(["en"]);
  }
 }, [store, storeLoading]);

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError(null);
  setSuccess(null);

  // English must always be selected
  if (!selectedLanguages.includes("en")) {
   setError(t("admin.languageSettings.englishRequired"));
   return;
  }

  setSaving(true);
  try {
   await api.put("/store/language-settings", {
    supportedLanguages: selectedLanguages,
   });
   setSuccess(t("admin.languageSettings.settingsUpdated"));
   await refreshStore();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.languageSettings.errors.update"));
  } finally {
   setSaving(false);
  }
 };

 const toggleLanguage = (langCode: string) => {
  // English cannot be disabled
  if (langCode === "en") {
   return;
  }

  setSelectedLanguages((prev) => {
   if (prev.includes(langCode)) {
    return prev.filter((l) => l !== langCode);
   } else {
    return [...prev, langCode].sort((a, b) => {
     const order = ["en", "de", "ar"];
     return order.indexOf(a) - order.indexOf(b);
    });
   }
  });
 };

 if (loading) {
  return (
   <Container>
    <Stack spacing="lg">
     <Spinner />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <Heading size="xl">{t("admin.languageSettings.title")}</Heading>
    <p className="text-gray-600 dark:text-gray-400">
     {t("admin.languageSettings.description")}
    </p>

    {error && <Alert variant="error">{error}</Alert>}
    {success && <Alert variant="success">{success}</Alert>}

    <Card>
     <form onSubmit={handleSubmit}>
      <Stack spacing="lg">
       <div>
        <Heading size="md" className="mb-4">{t("admin.languageSettings.title")}</Heading>
        <div className="space-y-3">
         {AVAILABLE_LANGUAGES.map((lang) => {
          const isSelected = selectedLanguages.includes(lang.code);
          const isEnglish = lang.code === "en";

          return (
           <div
            key={lang.code}
            className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-colors ${
             isSelected
              ? "border-indigo-500 bg-blue-50 dark:bg-blue-900/20"
              : "border-gray-200 hover:border-gray-300"
            } ${isEnglish ? "opacity-100" : ""}`}
            onClick={() => !isEnglish && toggleLanguage(lang.code)}
           >
            <input
             type="checkbox"
             checked={isSelected}
             onChange={() => !isEnglish && toggleLanguage(lang.code)}
             disabled={isEnglish}
             className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer disabled:opacity-50"
            />
            <span className="text-2xl">{lang.flag}</span>
            <div className="flex-1">
             <div className="font-medium text-gray-900 dark:text-gray-100">{lang.label}</div>
             {isEnglish && (
              <div className="text-xs text-gray-500 dark:text-gray-400">{t("admin.languageSettings.englishRequired")}</div>
             )}
            </div>
            {isSelected && (
             <svg
              className="w-5 h-5 text-indigo-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
             >
              <path
               strokeLinecap="round"
               strokeLinejoin="round"
               strokeWidth={2}
               d="M5 13l4 4L19 7"
              />
             </svg>
            )}
           </div>
          );
         })}
        </div>
       </div>

       <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={saving}>
         {saving ? t("admin.languageSettings.saving") : t("admin.languageSettings.save")}
        </Button>
        <Button
         type="button"
         variant="secondary"
         onClick={() => {
          setSelectedLanguages((store?.supportedLanguages as string[]) || ["en"]);
          setError(null);
          setSuccess(null);
         }}
         disabled={saving}
        >
         {t("common.cancel")}
        </Button>
       </div>
      </Stack>
     </form>
    </Card>
   </Stack>
  </Container>
 );
};

